/**
 * MED-1146 — the ONE participant session-label rule, shared across the
 * flag-gated MED02 surfaces (refresh dashboard, pre/post-session headers, and
 * the Next Session sidebar card).
 *
 * The *shape* mirrors the coach analog (MED-1106); the *value* is participant
 * display-aware — the backend resolves the topic via `DISPLAY_AWARE_TOPIC_EXPR`
 * (`display_name` first), so the CM's per-cohort label (MED-832) wins.
 *
 * Rule:
 *  - Topic present       → `Session N: {Topic}`
 *  - Else group present  → `Session N: {group}` (composed HERE, never the raw
 *    `{group} - Session N` title the server stores)
 *  - Workshop / no topic & no group → the raw title stands alone (it IS the
 *    workshop's name), and is NEVER prefixed with `Session N:`
 *
 * Composing onto `topic ?? group` (and never onto the raw title) is what kills
 * the residual double-label MED-1134 left behind (AC-L5): "Session 2: Peer01-01
 * - Session 2". The server exposes a clean, nullable topic + the group + raw
 * title; this helper owns the shape so no surface can re-create the double-label.
 */
export interface SessionLabelParts {
  session_number: number | null;
  /** Display-aware resolved topic (DISPLAY_AWARE_TOPIC_EXPR); null when unset. */
  session_topic?: string | null;
  /** Group name — the fallback when there is no topic (composed, not raw). */
  group_name?: string | null;
  /** Raw `sessions.title` — workshop title / last-resort standalone fallback. */
  title?: string | null;
}

/** Trim and treat blank as absent so an empty string never wins the COALESCE. */
function clean(value: string | null | undefined): string | null {
  if (value == null) return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

export function composeSessionLabel(parts: SessionLabelParts): string {
  const topic = clean(parts.session_topic);
  const group = clean(parts.group_name);
  const rawTitle = clean(parts.title);

  // Topic (display-aware) wins, then the group name — both get the composed
  // "Session N:" shape. We never compose onto the raw title (AC-L5).
  const composable = topic ?? group;
  if (composable) {
    return parts.session_number != null
      ? `Session ${parts.session_number}: ${composable}`
      : composable;
  }

  // No topic and no group → the workshop (or 1:1) title stands alone. Never
  // prefix it — prefixing the raw title is the bug this story removes.
  if (rawTitle) return rawTitle;

  // Ultimate guard — a session should always have a title; avoid an empty label.
  return parts.session_number != null ? `Session ${parts.session_number}` : '';
}
